import { AddressInputComponentOut, AddressInputContainer } from "@/models/interfaces/entity-settings";

// Каноническая форма адреса для payload'а поиска контактов. Совпадает с тем, что
// сервер ожидает в теле POST persons/search/main|client|project и в InputContainer
// у NewContact (create/update).
export type RequestAddressContainer = {
    components: { addressNodeId: number; level: number; value: string }[];
    activeLevel: number;
};

// Цепочку из AddressSelectorRefactored (@chainSelected, PascalCase) приводим к
// каноническому объекту (camelCase), оставляя только заполненные уровни.
// Возвращаем null для пустого адреса — чтобы обычный поиск по имени/тегам не менялся.
export function chainToRequestContainer(
    chain: AddressInputContainer | null
): RequestAddressContainer | null {
    if (!chain) {
        return null;
    }
    const components =
        chain.Components
            .filter((c) => (c.Value ?? "").trim().length > 0)
            .map((c) => ({ addressNodeId: c.AddressNodeId, level: c.Level, value: c.Value }));
    if (!components.length) {
        return null;
    }
    return { components, activeLevel: chain.ActiveLevel };
}

// Форма адреса для ЗАПИСИ контакта (create/update): сервер требует ВСЕ уровни
// структуры, незаполненные — с value: "" (так делал старый Angular-клиент).
// Обратно пустые уровни не приходят, поэтому добиваем контейнер структурой
// из кеша auxStore — того же, из которого селектор клонирует свой container.
export function toFullRequestContainer(
    container: RequestAddressContainer | null,
    structure: AddressInputComponentOut[]
): RequestAddressContainer {
    if (!structure.length) {
        return container ?? { components: [], activeLevel: 0 };
    }
    const valueByLevel = new Map(
        (container?.components ?? []).map((c) => [c.level, c.value ?? ""])
    );
    return {
        components: structure.map((s) => ({
            addressNodeId: s.AddressNodeId,
            level: s.Level,
            value: valueByLevel.get(s.Level) ?? "",
        })),
        activeLevel: container?.activeLevel ?? 0,
    };
}

// Сколько первых уровней адреса обязательны по умолчанию
export const DEFAULT_REQUIRED_ADDRESS_LEVELS = 3;

// Номера обязательных уровней адреса, которые не заполнены.
// Пустой массив — адрес пригоден для записи контакта.
// Обязательными считаются первые minRequiredLevels уровней структуры ПЛЮС всё,
// что сервер пометил Required
export function missingRequiredAddressLevels(
    container: RequestAddressContainer | null,
    structure: AddressInputComponentOut[],
    minRequiredLevels: number = DEFAULT_REQUIRED_ADDRESS_LEVELS
): number[] {
    const valueByLevel = new Map(
        (container?.components ?? []).map((c) => [c.level, (c.value ?? "").trim()])
    );
    // structure — общий массив из кеша auxStore, сортируем копию.
    const byLevelAsc = [...structure].sort((a, b) => a.Level - b.Level);
    const requiredByDefault = new Set(
        byLevelAsc.slice(0, Math.max(0, minRequiredLevels)).map((s) => s.Level)
    );

    return byLevelAsc
        .filter((s) => (s.Required || requiredByDefault.has(s.Level)) && !valueByLevel.get(s.Level))
        .map((s) => s.Level);
}
